import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { chatsApi, filesApi } from "../../api/chats";
import { ApiError } from "../../api/http";
import type { Chat } from "../../api/types";
import { toast } from "../../ui/toast";
import { meQuery } from "../auth/me";

export const useMe = () => useQuery(meQuery).data ?? null;

export const useChats = () => useQuery({ queryKey: ["chats"], queryFn: chatsApi.list });

export const useMessages = (chatId: string | undefined) =>
  useQuery({
    queryKey: ["messages", chatId],
    queryFn: () => chatsApi.messages(chatId!),
    enabled: !!chatId,
    staleTime: 10_000,
  });

/**
 * Файлы пользователя. Пока в Hub нет API файлов (404), вложения скрыты:
 * клиент выкатывается раньше, чем файлы в Hub, и включается сам.
 */
export const useFiles = () => {
  const q = useQuery({
    queryKey: ["files"],
    queryFn: () => filesApi.list(),
    retry: false,
    staleTime: 15_000,
  });
  const unavailable = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 405);
  return { ...q, available: !unavailable, files: q.data ?? [] };
};

export function useRenameChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => chatsApi.rename(id, title),
    onSuccess: (chat) => qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.id === chat.id ? chat : c))),
  });
}

export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => chatsApi.remove(id),
    onSuccess: (_r, id) => {
      qc.setQueryData<Chat[]>(["chats"], (old) => old?.filter((c) => c.id !== id));
      qc.removeQueries({ queryKey: ["messages", id] });
      void qc.invalidateQueries({ queryKey: ["files"] });
    },
  });
}

/** Закрепить, открепить, поменять метки. Сразу в списке, при ошибке — откат и сообщение. */
export function useUpdateChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { pinned?: boolean; tags?: string[] } }) => chatsApi.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ["chats"] });
      const prev = qc.getQueryData<Chat[]>(["chats"]);
      qc.setQueryData<Chat[]>(["chats"], (old) =>
        old?.map((c) =>
          c.id !== id
            ? c
            : {
                ...c,
                ...(patch.tags ? { tags: patch.tags } : {}),
                ...(patch.pinned !== undefined ? { pinnedAt: patch.pinned ? (c.pinnedAt ?? new Date().toISOString()) : null } : {}),
              },
        ),
      );
      return { prev };
    },
    // Строка истории может пересоздаться (чат переехал в «Закреплённые»), поэтому ошибку показываем здесь, а не у вызова.
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["chats"], ctx.prev);
      toast("Не удалось изменить чат. Попробуйте ещё раз.");
    },
    onSuccess: (chat) => qc.setQueryData<Chat[]>(["chats"], (old) => old?.map((c) => (c.id === chat.id ? chat : c))),
  });
}

/** Значение с задержкой: запрос к поиску уходит, когда человек перестал печатать. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Поиск по переписке всех чатов на Hub. От двух символов, с задержкой, прошлый результат виден до нового. */
export function useHistorySearch(q: string) {
  const term = useDebounced(q.trim(), 300);
  const enabled = term.length >= 2;
  const query = useQuery({
    queryKey: ["search", term],
    queryFn: ({ signal }) => chatsApi.search(term, signal),
    enabled,
    staleTime: 15_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
  return { ...query, enabled, term, pending: enabled && (query.isFetching || term !== q.trim()) };
}

/** Закреплённые сверху (последние закреплённые выше), дальше — по последней переписке. */
export function orderChats(list: Chat[]): Chat[] {
  const pinned = list.filter((c) => c.pinnedAt).sort((a, b) => b.pinnedAt!.localeCompare(a.pinnedAt!));
  return [...pinned, ...list.filter((c) => !c.pinnedAt)];
}
